<template>
  <SectionPageShell section="organisations" title="Organisationen">
    <DataTable
      :paginator="true"
      :lazy="true"
      :rows="clientStore.serverOptions.rows"
      :totalRecords="totalRecords"
      dataKey="id"
      :value="clients"
      paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
      :globalFilterFields="['filter_kdnr','name']"
      :rowsPerPageOptions="[10,20,50]"
      currentPageReportTemplate="Zeige {first} bis {last} von {totalRecords}"
      v-model:filters="filters"
      filterMatchModes="contains"
      filterDisplay="row"
      @page="onPage($event)"
      @filter="onFilter($event)"
      responsiveLayout="scroll"
    >
      <template #empty>
        Keine Organisationen gefunden.
      </template>

      <TableColumn field="filter_kdnr" :showFilterMenu="false" header="Kundennummer">
        <template #body="slotProps">
          {{ slotProps.data.kdnr }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText v-model="filterModel.value" type="text" @input="filterCallback()" class="p-column-filter" placeholder="Kundennummer suchen" />
          <template v-if="filterModel.value">
            <Icon icon="fa-pro-solid:circle-x" class="ms-3 hover-pointer" @click="filterModel.value = null; filterCallback()" />
          </template>
        </template>
      </TableColumn>
      <TableColumn field="name" header="Name" :showFilterMenu="false">
        <template #filter="{ filterModel, filterCallback }">
          <InputText v-model="filterModel.value" type="text" @input="filterCallback()" class="p-column-filter" placeholder="Name suchen" />
          <template v-if="filterModel.value">
            <Icon icon="fa-pro-solid:circle-x" class="ms-3 hover-pointer" @click="filterModel.value = null; filterCallback()" />
          </template>
        </template>
      </TableColumn>
      <TableColumn field="account_type" header="Typ" />
      <TableColumn field="active" header="Aktiv">
        <template #body="{data}">
          <ShowBool :bool="data.active" />
        </template>
      </TableColumn>
      <TableColumn header="Aktionen">
        <template #body="{data}">
          <div class="d-flex">
            <template v-if="data.account_type !== 'Location' && showUseOrganisationAction">
              <span v-if="isCurrentOrganisation(data) && !userStore.user.isStaffMember()" class="text-muted">
                aktuell verwendet
              </span>
              <button
                v-else-if="userStore.user.isStaffMember() && data.impersonation_available"
                type="button"
                class="btn btn-link p-0 me-2 text-decoration-none"
                title="Impersonate this organization"
                :disabled="impersonatingClientId === data.id"
                @click="impersonateOrganisation(data)"
              >
                <Icon :icon="impersonatingClientId === data.id ? 'fa-pro-solid:spinner-third' : 'fa-pro-solid:user-secret'" :class="{'fa-spin': impersonatingClientId === data.id}" />
                {{ impersonatingClientId === data.id ? 'Impersonating...' : 'Impersonate' }}
              </button>
              <a v-else-if="!userStore.user.isStaffMember()" :href="`?kdnr=${data.kdnr}`" class="me-2" title="Organisation verwenden" @click.prevent="useOrganisation(data)">
                <Icon icon="fa-pro-solid:circle-arrow-right" />
                verwenden
              </a>
            </template>
          </div>
        </template>
      </TableColumn>
    </DataTable>
  </SectionPageShell>
</template>

<script>
import SectionPageShell from "@/components/base_type/SectionPageShell.vue";
import {useClientStore} from "@/stores/client";
import {useUserStore} from "@/stores/user";
import InputText from "primevue/inputtext";
import {FilterMatchMode} from "@primevue/core/api";
import ShowBool from "@/components/ShowBool.vue";

export default {
  components: {
    InputText,
    SectionPageShell,
    ShowBool,
  },
  setup() {
    return {
      clientStore: useClientStore(),
      userStore: useUserStore(),
    }
  },
  data() {
    return {
      filters: {
        filter_kdnr: { value: null, matchMode: FilterMatchMode.EQUALS },
        name: { value: null, matchMode: FilterMatchMode.CONTAINS },
      },
      clients: [],
      totalRecords: 0,
      timer: null,
      impersonatingClientId: null,
    }
  },
  computed: {
    showUseOrganisationAction() {
      return this.totalRecords > 1 || this.userStore.user.isStaffMember()
    },
  },
  created() {
    const initialFilters = { ...this.filters }

    if (this.$route.query.filter_kdnr) {
      initialFilters.filter_kdnr.value = this.$route.query.filter_kdnr
    }
    if (this.$route.query.name) {
      initialFilters.name.value = this.$route.query.name
    }

    this.filters = initialFilters
    this.clientStore.serverOptions.filters = initialFilters
    this.getClients()
  },
  methods: {
    onPage(event) {
      this.clientStore.serverOptions = event
      this.getClients()
    },
    getClients() {
      this.loading = true
      this.clientStore.apiIndex().then(({data}) => {
        this.clients = data.clients
        this.totalRecords = data.totalRecords
      }).catch(console.error)
    },
    onFilter(data) {
      if (this.timer) {
        clearTimeout(this.timer)
      }
      this.timer = setTimeout(() => {
        this.clientStore.serverOptions.filters = data.filters
        this.getClients()
      }, 500)
    },
    isCurrentOrganisation(org) {
      return String(this.userStore.user.organisation?.kdnr) === String(org?.kdnr)
    },
    useOrganisation(org) {
      this.userStore.user.setOrganisation(org)
      this.$router.push({
        query: {
          ...this.$route.query,
          kdnr: org.kdnr,
        },
      })
    },
    async impersonateOrganisation(org) {
      if (!org?.id || this.impersonatingClientId) {
        return
      }

      this.impersonatingClientId = org.id
      try {
        await this.userStore.apiImpersonateClient(org.id)
        await this.$router.push({
          name: 'dashboard',
          query: {
            kdnr: org.kdnr,
          },
        })
      } finally {
        this.impersonatingClientId = null
      }
    },
  },
}
</script>
